
import { useCallback } from 'react';
import { useRefreshReader } from 'customHooks/reader';
import { useReadAnnotations, useCreateAnnotation, useUpdateAnnotation } from 'customHooks/db';
import { useSyncClassPreparationDispatcher } from 'customHooks/syncClassPreparation';
import { useStore, StoreTypes } from 'context';
import Repository from 'repositories/Repository';
import { AnnotationType } from 'constants/annotationTypes';
import { mergeCanvasJSON } from 'util/svg';
import { PreparationAPI } from 'api/PreparationsAPI'
import { EventBus } from 'events/EventBus';
import { ReaderEvent } from 'events/EventTypes';
const { PreparationRepository } = Repository;

export const usePreparation = () => {
    const { readAnnotations, readAnnotationById } = useReadAnnotations();
    const createAnnotation = useCreateAnnotation();
    const updateAnnotation = useUpdateAnnotation();
    const { syncClassPreparation, syncSinglePreparation } = useSyncClassPreparationDispatcher();

    const getPreparationList = useCallback(async (bookId, token, annotationType) => {
        let progressCount = 0;
        let progressMax;
        const params = { bookIds: [bookId] }
        const cloudData = await PreparationRepository.getPreparationList({ params, token });
        const localData = await readAnnotations({ bookId, annotationType });
        const cloudDataId = cloudData.filter(data => !data.isDeleted).map(data => data.id);
        const localDataId = localData.map(data => data.id);
        progressMax = cloudDataId.length + localDataId.length;
        for (let data of cloudData) {
            let { id, bookId, name, marks, type, pageIndex, updatedAt, isDeleted, isDoublePageMode, canvasSVGObjects,extendedContentAnnotation } = data;
            let annotations = [];
            const checkObj = localData.find((localData) => { return data.id === localData.id });


            if (!checkObj) {
                if (!data.isDeleted) {
                    //annotations = await PreparationRepository.getPreparationAnnotations({ id, token });
                    await createAnnotation({
                        id,
                        bookId,
                        name,
                        annotations,
                        marks,
                        type,
                        pageIndex,
                        isDoublePageMode,
                        lastSyncedAt: updatedAt,
                        canvasSVGObjects,
                        extendedContentAnnotation,
                        isDeleted,
                        updatedAt,
                        // fabric annotations 於此不 eager 載入（annotations=[]），改由 updatePreparationAnnotation
                        // 逐頁延遲載入。必須標記 isUpdate:true，否則 updatePreparationAnnotation 會 early return，
                        // 導致此類（雲端有、本機無）的 record 永遠不載 fabric。
                        isUpdate: true
                    });
                    progressCount++;
                    EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                }
            } else if (data.updatedAt > checkObj.lastSyncedAt) {
                if (checkObj.isDirty) {
                    const remoteAnnotations = await PreparationRepository.getPreparationAnnotations({ id, token });
                    annotations = remoteAnnotations.map(record => {
                        let localAnnotation = checkObj.annotations.find(v => v.pageIndex === record.pageIndex);
                        const annotation=(record&&localAnnotation)?JSON.stringify(mergeCanvasJSON(JSON.parse(record.annotation), JSON.parse(localAnnotation.annotation))):''
                        if (localAnnotation) {
                            return {
                                pageIndex: record.pageIndex,
                                annotation: annotation
                            };
                        } else {
                            return record;
                        }
                    });
                    annotations = annotations.concat(checkObj.annotations.reduce((acc, record) => {
                        if (!annotations.some(annotation => annotation.pageIndex === record.pageIndex)) {
                            acc.push(record);
                        }
                        return acc;
                    }, []));
                    marks = marks.concat(checkObj.marks).reduce((acc, mark) => {
                        if (!acc.some(v => v.id === mark.id)) {
                            acc.push(mark);
                        }
                        return acc;
                    }, []);
                    canvasSVGObjects = canvasSVGObjects.concat(checkObj.canvasSVGObjects).reduce((acc, canvasSVGObject) => {
                        if (!acc.some(v => v.id === canvasSVGObject.id)) {
                            acc.push(canvasSVGObject);
                        }
                        return acc;
                    }, []);
                    extendedContentAnnotation = extendedContentAnnotation.concat(checkObj.extendedContentAnnotation)
                    // pageIndex = checkObj.pageIndex;
                }
                await updateAnnotation(id, {
                    id,
                    bookId,
                    name,
                    annotations,
                    marks,
                    type,
                    pageIndex,
                    isDoublePageMode,
                    lastSyncedAt: updatedAt,
                    updatedAt,
                    canvasSVGObjects,
                    extendedContentAnnotation,
                    isDeleted,
                    isUpdate: true
                });
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            } else if (checkObj || data.isDeleted) {
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            }
        }
        if (annotationType !== AnnotationType.GUEST) {
            for (let data of localData) {
                const checkObj = (cloudData && cloudData.find((remoteData) => { return data.id === remoteData.id })) || null;
                if (!checkObj) {
                    syncClassPreparation(data.id);
                    progressCount++;
                    EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                } else {
                    if (data.updatedAt > checkObj.updatedAt) {
                        syncClassPreparation(data.id);
                        progressCount++;
                        EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                    } else {
                        progressCount++;
                        EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                    }
                }
            }
        }
        return await readAnnotations({ bookId });
    }, [createAnnotation, readAnnotations, syncClassPreparation, updateAnnotation]);

    const updatePreparationAnnotation = useCallback(async ({ id, token }) => {
        const localData = await readAnnotationById({ id });
        if (!localData || !localData.isUpdate) return;
        localData.annotations = await PreparationRepository.getPreparationAnnotations({ id, token });
        localData.isUpdate = false;
        const annotations = await updateAnnotation(id, localData)
    }, [updateAnnotation, readAnnotationById])

    const copyPreparationAnnotation = useCallback(async ({ annotation, token }) => {
        await PreparationRepository.copyPreparationAnnotations({ annotation, token });
    }, [])

    const syncPreparationForOffLine = useCallback(async (bookId, token, annotationType) => {
        let progressCount = 0;
        let progressMax;
        let cloudData = [];
            try {
                const response = await PreparationAPI.get(
                    `${process.env.REACT_APP_API_DOMAIN}/preparations`,
                    { bookIds: [bookId] },
                    token
                );
                if (response.status === 'success') {
                    cloudData = response.data;
                }
            } catch (err) {
                console.log(`Electron sync annotation -- GET /preparations error ${err}`);
            }
        let localData = await readAnnotations({ bookId, annotationType });
        const cloudDataId = cloudData.filter(data => !data.isDeleted).map(data => data.id);
        const localDataId = localData.map(data => data.id);
        progressMax = cloudDataId.length + localDataId.length;

        // 先濾掉標示為刪除的檔案，減少檢查次數
        // cloudData = cloudData.filter(data => !data.isDeleted);
        // localData = localData.filter(data => !data.isDeleted);

        // 本機有未上傳變更、且這次已與雲端合併的紀錄，合併結果需再上傳
        const mergedIds = new Set();

        for (let data of cloudData) {
            let { id, bookId, name, marks, pageIndex, updatedAt, isDeleted, isDoublePageMode, canvasSVGObjects,extendedContentAnnotation } = data;
            let annotations = [];
            const checkObj = localData.find((localData) => { return data.id === localData.id });

            if (!checkObj) {
                if (!data.isDeleted) {
                    annotations = await PreparationRepository.getPreparationAnnotations({ id, token });
                    // 下載失敗時不建立，否則 lastSyncedAt 已對齊雲端，之後永遠不會再下載
                    if (!annotations) {
                        progressCount++;
                        EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                        continue;
                    }
                    await createAnnotation({
                        id,
                        bookId,
                        name,
                        annotations,
                        marks,
                        type: AnnotationType.GUEST,
                        pageIndex,
                        isDoublePageMode,
                        lastSyncedAt: updatedAt,
                        canvasSVGObjects,
                        extendedContentAnnotation,
                        isDeleted,
                        updatedAt
                    });
                    progressCount++;
                    EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                }
            } else if (data.updatedAt > (checkObj.lastSyncedAt || 0)) {
                // 以 lastSyncedAt 判斷雲端是否有新資料：本機 updatedAt 會被翻頁（setPageIndex）推成當下時間，
                // 用它比較會誤判本機較新而跳過下載
                const remoteAnnotations = await PreparationRepository.getPreparationAnnotations({ id, token });
                if (!remoteAnnotations) {
                    progressCount++;
                    EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
                    continue;
                }
                if (checkObj.isDirty) {
                    mergedIds.add(id);
                    annotations = remoteAnnotations.map(record => {
                        let localAnnotation = checkObj.annotations.find(v => v.pageIndex === record.pageIndex);
                        const annotation=(record&&localAnnotation)?JSON.stringify(mergeCanvasJSON(JSON.parse(record.annotation), JSON.parse(localAnnotation.annotation))):''
                        if (localAnnotation) {
                            return {
                                pageIndex: record.pageIndex,
                                annotation: annotation
                            };
                        } else {
                            return record;
                        }
                    });
                    annotations = annotations.concat(checkObj.annotations.reduce((acc, record) => {
                        if (!annotations.some(annotation => annotation.pageIndex === record.pageIndex)) {
                            acc.push(record);
                        }
                        return acc;
                    }, []));
                    marks = marks.concat(checkObj.marks).reduce((acc, mark) => {
                        if (!acc.some(v => v.id === mark.id)) {
                            acc.push(mark);
                        }
                        return acc;
                    }, []);
                    canvasSVGObjects = canvasSVGObjects.concat(checkObj.canvasSVGObjects).reduce((acc, canvasSVGObject) => {
                        if (!acc.some(v => v.id === canvasSVGObject.id)) {
                            acc.push(canvasSVGObject);
                        }
                        return acc;
                    }, []);
                    extendedContentAnnotation = extendedContentAnnotation.concat(checkObj.extendedContentAnnotation)
                    // pageIndex = checkObj.pageIndex;
                }
                await updateAnnotation(id, {
                    id,
                    bookId,
                    name,
                    annotations: annotations.length === 0 ? remoteAnnotations : annotations,
                    marks,
                    type: AnnotationType.GUEST,
                    pageIndex,
                    isDoublePageMode,
                    lastSyncedAt: updatedAt,
                    updatedAt,
                    canvasSVGObjects,
                    extendedContentAnnotation,
                    isDeleted,
                    isUpdate: true
                });
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            } else if (checkObj || data.isDeleted) {
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            }

        };
        // 上方可能已將雲端資料寫回本機，需重讀，避免把合併前的舊資料上傳覆蓋雲端
        localData = (await readAnnotations({ bookId, annotationType })).filter(data => localDataId.includes(data.id));
        for (let data of localData) {
            const checkObj = (cloudData && cloudData.find((remoteData) => { return data.id === remoteData.id })) || null;
            if (!checkObj) {
                // syncClassPreparation(data.id);
                if (data.annotations.length === 0) {
                    await syncSinglePreparation({
                        annotations: [],
                        localDB: { ...data, annotations: [], type: AnnotationType.CLASS_PREPARATION },
                        id: data.id
                    });
                } else {
                    await data.annotations.reduce((promiseChain, annotation) => {
                        return promiseChain.then(chainResults => {
                            return syncSinglePreparation({
                                annotations: [annotation],
                                localDB: { ...data, annotations: [], type: AnnotationType.CLASS_PREPARATION },
                                id: data.id
                            })
                        });
                    }, Promise.resolve({}));
                }
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            } else if (data.updatedAt > checkObj.updatedAt || data.isDeleted || mergedIds.has(data.id)) {
                // syncClassPreparation(data.id);
                if (data.annotations.length === 0) {
                    await syncSinglePreparation({
                        annotations: [],
                        localDB: { ...data, annotations: [], type: AnnotationType.CLASS_PREPARATION },
                        id: data.id
                    });
                } else {
                    await data.annotations.reduce((promiseChain, annotation) => {
                        return promiseChain.then(chainResults => {
                            return syncSinglePreparation({
                                annotations: [annotation],
                                localDB: { ...data, annotations: [], type: AnnotationType.CLASS_PREPARATION },
                                id: data.id
                            })
                        });
                    }, Promise.resolve({}));
                }
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            } else {
                progressCount++;
                EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: progressCount / progressMax * 100 } });
            }
        }
        // return null;
        return await readAnnotations({ bookId });
    }, [createAnnotation, readAnnotations, syncSinglePreparation, updateAnnotation])

    return {
        getPreparationList,
        updatePreparationAnnotation,
        copyPreparationAnnotation,
        syncPreparationForOffLine
    };
}




export const usePreparationSnapshot = () => {
    const reducers = useStore();
    const [{ activityInfo: { firestore } }] = reducers[StoreTypes.annotation];
    const [{ userId }] = reducers[StoreTypes.user];
    const updateAnnotation = useUpdateAnnotation();
    const { readAnnotationById } = useReadAnnotations();
    const { refreshReader } = useRefreshReader();

    const preparationSnapshot = useCallback(async (id, token) => {
        if (userId) {
            let preparationRef = firestore.collection(`/teachers/${userId}/classPreparations`).where('id', '==', id);
            preparationRef.onSnapshot(async (snapshot) => {
                const cloudData = await PreparationRepository.getPreparationById({ id, token });
                const localData = await readAnnotationById({ id });
                if (cloudData && localData) {
                    if (cloudData.updatedAt > localData.lastSyncedAt) {
                        const { marks, pageIndex, updatedAt, isDoublePageMode, canvasSVGObjects,extendedContentAnnotation } = cloudData;
                        const annotations = await PreparationRepository.getPreparationAnnotations({ id, token });
                        await updateAnnotation(id, {
                            annotations,
                            marks,
                            pageIndex,
                            lastSyncedAt: updatedAt,
                            updatedAt,
                            isDirty: false,
                            isDoublePageMode,
                            canvasSVGObjects,
                            extendedContentAnnotation
                        });
                        refreshReader(cloudData);
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    return { preparationSnapshot };
};
